import { z } from 'zod';
import { IdSchema, E164Schema, TimestampSchema, MoneySchema } from './common.js';

/**
 * The Node API contract (doc 05 section 10). Consumed only by arc-agent.
 *
 * CDRs arrive NORMALISED. FreeSWITCH's json_cdr is large, deeply nested and
 * version-specific -- parsing it belongs on the node, next to the switch that
 * produced it, not in the control plane. That keeps a FreeSWITCH upgrade from
 * becoming an arc-api change.
 */

export const NodeCdrSchema = z.object({
  /** FreeSWITCH channel UUID. The idempotency key for the whole pipeline. */
  id: IdSchema,
  call_uuid: IdSchema,
  tenant_id: IdSchema,
  direction: z.enum(['inbound', 'outbound', 'internal']),
  leg: z.enum(['a', 'b']).default('a'),

  from_number: z.string().max(64).nullable().optional(),
  from_name: z.string().max(128).nullable().optional(),
  to_number: z.string().max(64).nullable().optional(),
  destination_e164: E164Schema.nullable().optional(),
  extension_id: IdSchema.nullable().optional(),

  started_at: TimestampSchema,
  answered_at: TimestampSchema.nullable().optional(),
  ended_at: TimestampSchema,
  duration_sec: z.number().int().min(0),
  billable_sec: z.number().int().min(0).default(0),

  hangup_cause: z.string().max(64).nullable().optional(),
  sip_code: z.number().int().nullable().optional(),

  mos: z.string().nullable().optional(),
  jitter_ms: z.string().nullable().optional(),
  packet_loss_pct: z.string().nullable().optional(),
  codec: z.string().max(32).nullable().optional(),

  /** What the NODE computed, so the control plane can verify it (ADR-015). */
  rated_on_node: MoneySchema.nullable().optional(),
  rate_deck_version_id: IdSchema.nullable().optional(),

  recording_path: z.string().max(512).nullable().optional(),
});
export type NodeCdr = z.infer<typeof NodeCdrSchema>;

export const NodeCdrBatchSchema = z.object({
  cdrs: z.array(NodeCdrSchema).min(1).max(500),
}).strict();

export const NodeHeartbeatSchema = z.object({
  software: z.record(z.string()).optional(),
  active_calls: z.number().int().min(0).default(0),
  registrations: z.number().int().min(0).default(0),
  cdr_spool_depth: z.number().int().min(0).default(0),
}).strict();

/**
 * A node's live registration snapshot.
 *
 * `contact_ip` is the HANDSET, parsed from the Contact URL by the agent — not
 * the network peer, which is the proxy. Reporting the peer would show every
 * extension registered from one address.
 */
export const NodeRegistrationSchema = z.object({
  reg_user: z.string().max(128),
  realm: z.string().max(253),
  contact_uri: z.string().max(512).nullable().optional(),
  contact_ip: z.string().max(64).nullable().optional(),
  contact_port: z.number().int().min(1).max(65535).nullable().optional(),
  transport: z.string().max(16).nullable().optional(),
  expires_at: TimestampSchema.nullable().optional(),
  user_agent: z.string().max(256).nullable().optional(),
  ping_status: z.string().max(32).nullable().optional(),
  call_id: z.string().max(256).nullable().optional(),
});

export const NodeRegistrationBatchSchema = z.object({
  registrations: z.array(NodeRegistrationSchema).max(20000),
}).strict();

/**
 * Live telephony state pushed by a node.
 *
 * Ephemeral by design: it is never stored, only held in memory and fanned out.
 * The durable record of a call is its CDR.
 */
export const NodeLiveSnapshotSchema = z.object({
  at: TimestampSchema,
  extensions: z.array(z.object({
    user: z.string().max(32),
    domain: z.string().max(253),
    state: z.enum(['idle', 'ringing', 'on_call']),
    since: z.number().int(),
    direction: z.enum(['inbound', 'outbound', 'internal']).nullable(),
    peer: z.string().max(64).nullable(),
  })).max(5000),
  queues: z.array(z.object({
    queue: z.string().max(160),
    name: z.string().max(160),
    domain: z.string().max(253),
    waiting: z.number().int().min(0),
    agents_total: z.number().int().min(0),
    agents_available: z.number().int().min(0),
    agents_on_call: z.number().int().min(0),
    calls_answered: z.number().int().min(0),
    longest_wait_sec: z.number().int().min(0),
  })).max(500),
}).strict();

/**
 * Queue events from a node.
 *
 * mod_callcenter's own vocabulary, passed through rather than translated: the
 * distinctions it draws between abandoning, timing out and being cancelled are
 * not recoverable once collapsed into our own words.
 */
export const NodeQueueEventBatchSchema = z.object({
  events: z.array(z.object({
    queue_key: z.string().max(200),
    occurred_at: TimestampSchema,
    action: z.string().max(48),
    member_uuid: z.string().uuid().nullable().optional(),
    member_cid: z.string().max(64).nullable().optional(),
    agent: z.string().max(200).nullable().optional(),
    wait_sec: z.number().int().nullable().optional(),
    talk_sec: z.number().int().nullable().optional(),
    entry_position: z.number().int().nullable().optional(),
    exit_position: z.number().int().nullable().optional(),
    cause: z.string().max(64).nullable().optional(),
    cancel_reason: z.string().max(64).nullable().optional(),
    raw: z.record(z.string(), z.string()).optional(),
  })).max(500),
}).strict();
